import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { HttpParams } from '@angular/common/http';

import { QualityManagementService } from '../../../../services/quality-management.service';
import { CommonService } from '../../../../services/common.service';
import { ConfirmationDialogService } from '../../../../services/confirmation-dialog.service';
import { AlertNotificationService } from '../../../../services/alert-notification.service';

import {
  COMPANY_DOCUMENT_REVISION_STATUS_BADGES,
  COMPANY_DOCUMENT_REVISION_STATUS_LABELS,
  CompanyDocumentRevision,
  CompanyDocumentRevisionStatus,
  companyDocumentUserFullName
} from '../../../../models/entities/company-document';


@Component({
  selector: 'company-document-revisions',
  imports: [],
  templateUrl: './company-document-revisions.component.html',
  styleUrl: './company-document-revisions.component.scss'
})
export class CompanyDocumentRevisionsComponent implements OnInit, OnChanges {

  @Input() companyDocumentId?: string | number;
  @Input() canDelete: boolean = false;

  @Output() openRevision = new EventEmitter<CompanyDocumentRevision>();

  revisions: CompanyDocumentRevision[] = [];
  isLoading: boolean = false;

  constructor(
    private qualityManagementService: QualityManagementService,
    private commonService: CommonService,
    private confirmationDialogService: ConfirmationDialogService,
    private alertNotificationService: AlertNotificationService
  ) { }

  ngOnInit(): void {
    this.loadRevisions();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['companyDocumentId'] && !changes['companyDocumentId'].firstChange) {
      this.loadRevisions();
    }
  }

  reload(): void {
    this.loadRevisions();
  }

  statusLabel(revision: CompanyDocumentRevision): string {
    return COMPANY_DOCUMENT_REVISION_STATUS_LABELS[revision?.status as CompanyDocumentRevisionStatus] ?? '';
  }

  statusBadgeClass(revision: CompanyDocumentRevision): string {
    return COMPANY_DOCUMENT_REVISION_STATUS_BADGES[revision?.status as CompanyDocumentRevisionStatus] ?? 'bg-secondary';
  }

  reviewerLabel(revision: CompanyDocumentRevision): string {
    return companyDocumentUserFullName(revision?.reviewedUser) || '-';
  }

  approverLabel(revision: CompanyDocumentRevision): string {
    return companyDocumentUserFullName(revision?.approvedUser) || '-';
  }

  formatDate(value?: string | null): string {
    if (!value) {
      return '-';
    }

    const date = new Date(value);
    return isNaN(date.getTime()) ? '-' : this.commonService.formatDateOnly(date);
  }

  attachmentName(revision: CompanyDocumentRevision): string {
    return String(revision?.attachments?.[0]?.fileNameOriginal ?? '');
  }

  attachmentUrl(revision: CompanyDocumentRevision): string {
    return String(revision?.attachments?.[0]?.url ?? '');
  }

  hasAttachment(revision: CompanyDocumentRevision): boolean {
    return !!this.attachmentUrl(revision);
  }

  downloadAttachment(revision: CompanyDocumentRevision): void {
    const url = this.attachmentUrl(revision);

    if (!url) {
      this.alertNotificationService.warning('File URL not available');
      return;
    }

    this.qualityManagementService.attachmentPublicUrl(url).subscribe({
      next: (publicUrl: string) => {
        if (!publicUrl) {
          this.alertNotificationService.error('Error downloading file');
          return;
        }
        window.open(publicUrl, '_blank');
      },
      error: (error: any) => {
        console.error('Error downloading the attachment:', error);
        this.alertNotificationService.error('Error downloading file');
      }
    });
  }

  onOpen(revision: CompanyDocumentRevision): void {
    this.openRevision.emit(revision);
  }

  onDelete(revision: CompanyDocumentRevision): void {
    if (!this.canDelete || !revision?.companyDocumentRevisionId) {
      return;
    }

    this.confirmationDialogService.open(
      `Are you sure to delete revision ${revision.code}?`,
      () => this.deleteRevision(Number(revision.companyDocumentRevisionId))
    );
  }

  private deleteRevision(companyDocumentRevisionId: number): void {
    this.qualityManagementService.deleteCompanyDocumentRevision(companyDocumentRevisionId).subscribe({
      next: () => {
        this.alertNotificationService.success('Revision deleted.');
        this.loadRevisions();
      },
      error: (error: any) => {
        console.error('Error deleting the company document revision:', error);
        this.alertNotificationService.error(
          this.qualityManagementService.companyDocumentErrorMessage(error, 'Failed to delete the revision.')
        );
      }
    });
  }

  private loadRevisions(): void {
    if (!this.companyDocumentId) {
      this.revisions = [];
      return;
    }

    const params = new HttpParams().set('size', '9999');

    this.isLoading = true;
    this.qualityManagementService.listCompanyDocumentRevisions(Number(this.companyDocumentId), { params }).subscribe({
      next: (response: any) => {
        const revisions = this.qualityManagementService.readResponseKey(response, 'companyDocumentRevisions');
        this.revisions = Array.isArray(revisions) ? revisions : [];
        this.isLoading = false;
      },
      error: (error: any) => {
        console.error('Error loading the company document revisions:', error);
        this.revisions = [];
        this.isLoading = false;
      }
    });
  }
}
